import api from './api'

export const adminService = {
  // Dashboard
  getAdminDashboard: () => api.get('/admin/dashboard'),

  // Asset Management
  getAssets: (params) => api.get('/admin/assets', { params }),
  getAssetById: (id) => api.get(`/admin/assets/${id}`),
  createAsset: (assetData) => api.post('/admin/assets', assetData),
  updateAsset: (id, assetData) => api.put(`/admin/assets/${id}`, assetData),
  deleteAsset: (id, permanent = false) => api.delete(`/admin/assets/${id}`, { 
    params: { permanent } 
  }),

  // Document Repository
  getDocuments: (params) => api.get('/admin/documents', { params }),

  // System Settings
  getSystemSettings: (params) => api.get('/admin/settings', { params }),
  updateSystemSettings: (settings) => api.put('/admin/settings', settings),

  // User Management
  getAllUsers: (params) => {
    return api.get('/admin/users', { params });
  },

  createUser: (userData) => {
    return api.post('/admin/users', userData);
  },

  updateUser: (userId, userData) => {
    return api.put(`/admin/users/${userId}`, userData);
  },

  deleteUser: (userId) => {
    return api.delete(`/admin/users/${userId}`);
  },

  getUserById: (userId) => {
    return api.get(`/admin/users/${userId}`);
  },

  resetUserPassword: (userId, passwordData) => {
    return api.put(`/admin/users/${userId}/reset-password`, passwordData);
  },

  // User Type Management (using admin endpoints for consistency)
  getUserTypes: (params) => api.get('/admin/user-types', { params }),
  getUserTypeById: (id) => api.get(`/admin/user-types/${id}`),
  createUserType: (userTypeData) => api.post('/admin/user-types', userTypeData),
  updateUserType: (id, userTypeData) => api.put(`/admin/user-types/${id}`, userTypeData),
  deleteUserType: (id) => api.delete(`/admin/user-types/${id}`),
  assignUserType: (userId, userTypeId) => api.patch(`/admin/users/${userId}/user-type`, { user_type_id: userTypeId }),
  getUsersByType: (userTypeId, params) => api.get(`/admin/user-types/${userTypeId}/users`, { params }),
  getAvailableModules: () => api.get('/admin/modules'),
  validateUserTypePermissions: (permissions) => api.post('/admin/user-types/validate-permissions', { permissions }),

  // Permissions Management
  getPermissions: () => api.get('/admin/permissions'),
  updateUserPermissions: (data) => api.put('/admin/permissions', data),

  // Roles Management
  getRoles: () => api.get('/admin/roles'),
  createRole: (roleData) => api.post('/admin/roles', roleData),
  updateRole: (id, roleData) => api.put(`/admin/roles/${id}`, roleData),
  deleteRole: (id) => api.delete(`/admin/roles/${id}`),



  // Audit Logs
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),

  // Backup Management
  getBackups: (params) => api.get('/admin/backups', { params }),
  createBackup: (backupData) => api.post('/admin/backups', backupData),
  deleteBackup: (id) => api.delete(`/admin/backups/${id}`),

  // System Monitoring
  getSystemHealth: () => api.get('/admin/system-health'),
  getDatabaseStats: () => api.get('/admin/database-stats'),

  // Notifications
  createNotification: (notificationData) => api.post('/admin/notifications', notificationData),

  // Utility functions
  getAssetCategories: () => Promise.resolve({
    data: {
      data: {
        categories: [
          { value: 'computer', label: 'Computer Equipment' },
          { value: 'furniture', label: 'Furniture' },
          { value: 'vehicle', label: 'Vehicles' },
          { value: 'machinery', label: 'Machinery' },
          { value: 'software', label: 'Software' },
          { value: 'mobile', label: 'Mobile Devices' },
          { value: 'network', label: 'Network Equipment' },
          { value: 'office', label: 'Office Equipment' },
          { value: 'security', label: 'Security Equipment' },
          { value: 'other', label: 'Other' }
        ]
      }
    }
  }),

  getAssetStatuses: () => Promise.resolve({
    data: {
      data: {
        statuses: [
          { value: 'active', label: 'Active', color: 'green' },
          { value: 'inactive', label: 'Inactive', color: 'gray' },
          { value: 'maintenance', label: 'Under Maintenance', color: 'yellow' },
          { value: 'disposed', label: 'Disposed', color: 'red' },
          { value: 'lost', label: 'Lost', color: 'orange' },
          { value: 'stolen', label: 'Stolen', color: 'red' }
        ]
      }
    }
  }),

  getDepreciationMethods: () => Promise.resolve({
    data: {
      data: {
        methods: [
          { value: 'straight_line', label: 'Straight Line' },
          { value: 'declining_balance', label: 'Declining Balance' },
          { value: 'sum_of_years', label: 'Sum of Years Digits' },
          { value: 'none', label: 'No Depreciation' }
        ]
      }
    }
  }),

  getBackupTypes: () => Promise.resolve({
    data: {
      data: {
        types: [
          { value: 'full', label: 'Full Backup', description: 'Complete backup of all data' },
          { value: 'incremental', label: 'Incremental Backup', description: 'Backup of changes since last backup' },
          { value: 'differential', label: 'Differential Backup', description: 'Backup of changes since last full backup' }
        ]
      }
    }
  }),

  getNotificationTypes: () => Promise.resolve({
    data: {
      data: {
        types: [
          { value: 'info', label: 'Information', color: 'blue' },
          { value: 'success', label: 'Success', color: 'green' },
          { value: 'warning', label: 'Warning', color: 'yellow' },
          { value: 'error', label: 'Error', color: 'red' }
        ]
      }
    }
  }),

  getTargetTypes: () => Promise.resolve({
    data: {
      data: {
        types: [
          { value: 'all', label: 'All Users' },
          { value: 'users', label: 'Specific Users' },
          { value: 'departments', label: 'Departments' },
          { value: 'roles', label: 'Roles' }
        ]
      }
    }
  }),

  getUserTypeColors: () => Promise.resolve({
    data: {
      data: {
        colors: [
          { value: 'blue', label: 'Blue', hex: '#3B82F6' },
          { value: 'green', label: 'Green', hex: '#10B981' },
          { value: 'purple', label: 'Purple', hex: '#8B5CF6' },
          { value: 'yellow', label: 'Yellow', hex: '#F59E0B' },
          { value: 'red', label: 'Red', hex: '#EF4444' },
          { value: 'gray', label: 'Gray', hex: '#6B7280' }
        ]
      }
    }
  }),

  getDefaultUserTypes: () => Promise.resolve({
    data: {
      data: {
        userTypes: [
          {
            name: 'super_admin',
            display_name: 'Super Administrator',
            description: 'Full system access with all permissions',
            color: 'red',
            is_system: true,
            permissions: {
              dashboard: ['read'],
              projects: ['read', 'create', 'update', 'delete'],
              inventory: ['read', 'create', 'update', 'delete'],
              hr: ['read', 'create', 'update', 'delete'],
              finance: ['read', 'create', 'update', 'delete', 'approve'],
              admin: ['read', 'create', 'update', 'delete', 'manage'],
              collaboration: ['read', 'create', 'update', 'delete'],
              file_share: ['read', 'create', 'update', 'delete', 'share'],
              documents: ['read', 'create', 'update', 'delete', 'approve']
            }
          },
          {
            name: 'hr_manager',
            display_name: 'HR Manager',
            description: 'Human Resources management with payroll access',
            color: 'blue',
            is_system: false,
            permissions: {
              dashboard: ['read'],
              hr: ['read', 'create', 'update', 'delete'],
              finance: ['read'],
              collaboration: ['read', 'create', 'update'],
              file_share: ['read', 'create', 'share'],
              documents: ['read', 'create', 'update']
            }
          },
          {
            name: 'finance_manager',
            display_name: 'Finance Manager',
            description: 'Financial management with approval authority',
            color: 'green',
            is_system: false,
            permissions: {
              dashboard: ['read'],
              finance: ['read', 'create', 'update', 'delete', 'approve'],
              hr: ['read'],
              collaboration: ['read', 'create'],
              file_share: ['read', 'create'],
              documents: ['read', 'create', 'approve']
            }
          },
          {
            name: 'project_manager',
            display_name: 'Project Manager',
            description: 'Project management and team coordination',
            color: 'purple',
            is_system: false,
            permissions: {
              dashboard: ['read'],
              projects: ['read', 'create', 'update', 'delete'],
              inventory: ['read'],
              collaboration: ['read', 'create', 'update', 'delete'],
              file_share: ['read', 'create', 'update', 'share'],
              documents: ['read', 'create', 'update']
            }
          },
          {
            name: 'employee',
            display_name: 'Regular Employee',
            description: 'Standard employee access with self-service',
            color: 'gray',
            is_system: false,
            permissions: {
              dashboard: ['read'],
              collaboration: ['read', 'create'],
              file_share: ['read', 'create'],
              documents: ['read']
            }
          }
        ]
      }
    }
  }),

  getSystemCategories: () => Promise.resolve({
    data: {
      data: {
        categories: [
          { value: 'general', label: 'General Settings' },
          { value: 'company', label: 'Company Information' },
          { value: 'system', label: 'System Configuration' },
          { value: 'security', label: 'Security Settings' },
          { value: 'email', label: 'Email Configuration' },
          { value: 'notification', label: 'Notification Settings' },
          { value: 'backup', label: 'Backup Settings' },
          { value: 'integration', label: 'Integration Settings' }
        ]
      }
    }
  }),

  // User Type Utilities
  formatUserTypePermissions: (permissions) => {
    const moduleNames = {
      dashboard: 'Dashboard',
      projects: 'Projects',
      inventory: 'Inventory',
      hr: 'Human Resources',
      finance: 'Finance',
      admin: 'Administration',
      collaboration: 'Collaboration',
      file_share: 'File Sharing',
      documents: 'Document Management'
    };
    
    return Object.entries(permissions).map(([moduleId, perms]) => ({
      module: moduleNames[moduleId] || moduleId,
      permissions: perms,
      count: perms.length
    }));
  },

  validateUserTypeData: (userTypeData) => {
    const errors = [];
    
    if (!userTypeData.name || userTypeData.name.trim().length === 0) {
      errors.push('User type name is required');
    }
    
    if (!userTypeData.display_name || userTypeData.display_name.trim().length === 0) {
      errors.push('Display name is required');
    }
    
    if (userTypeData.name && !/^[a-z_]+$/.test(userTypeData.name)) {
      errors.push('Name must contain only lowercase letters and underscores');
    }
    
    if (!userTypeData.permissions || Object.keys(userTypeData.permissions).length === 0) {
      errors.push('At least one module permission must be assigned');
    }
    
    return errors;
  }
}

export default adminService